import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import {
  RowSelectionContext,
  RowSelectionStore,
} from "@/components/data-table/context/RowSelectionContext";
import { List } from "@/components/list/List";
import { useProjectStoresQuery } from "@/modules/projects/hooks/useProjectStoresQuery";
import { memo, useMemo } from "react";
import { DisplayProjectStore } from "@/types/projectStore";
import { displayProjectStore } from "@/schemas/projectStore";
import { useTranslation } from "react-i18next";
import { FilterType } from "@/lib/filters";

interface StoreProjectAssigneesCreatorStoresProps {
  projectId: string;
  store: RowSelectionStore;
}

export const StoreProjectAssigneesCreatorStores = memo(
  ({ projectId, store }: StoreProjectAssigneesCreatorStoresProps) => {
    const { t } = useTranslation();
    const contextValue = useMemo(() => ({ store }), [store]);

    const filters = useMemo(
      () =>
        projectId
          ? [
              {
                id: "project_id",
                value: { value: projectId, filter: FilterType.EqualsString },
              },
            ]
          : [],
      [projectId],
    );
    const { query: projectStoresQuery, options: projectStoresOptions } =
      useProjectStoresQuery({
        projectId,
        name: "storeProjectAssigneesCreatorStores",
        enabled: Boolean(projectId),
        filters,
      });

    if (!projectId) {
      return (
        <p className="text-sm text-muted-foreground py-4">
          {t("storeProjectAssignees.title.stores")} –{" "}
          {t("storeProjectAssignees.creator.selectProjectFirst")}
        </p>
      );
    }

    return (
      <RowSelectionContext.Provider value={contextValue}>
        <DataTableQueryContextProvider value={{ query: projectStoresQuery }}>
          <List<DisplayProjectStore>
            schema={displayProjectStore}
            name="storeProjectAssigneesCreatorStores"
            translationKey="projectStores"
            query={projectStoresQuery}
            options={projectStoresOptions}
            tableFeatures={{
              enableRowSelection: true,
              enableSelectAll: true,
            }}
          >
            <div className="flex flex-row items-center gap-2">
              <List.Menubar className="grow">
                <List.Menubar.Columns />
                <List.Menubar.Reset />
              </List.Menubar>
            </div>
            <div className="flex flex-row grow gap-2 overflow-hidden">
              <List.Content className="overflow-x-auto flex-grow">
                <List.Status>
                  <List.SelectedRowsStatus />
                </List.Status>
              </List.Content>
            </div>
          </List>
        </DataTableQueryContextProvider>
      </RowSelectionContext.Provider>
    );
  },
);
